// Adding nextDaySchedule to the formData state and form UI
import React, { useState, useEffect, useRef } from 'react';
import Navbar from './Navbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { useSearchParams } from 'react-router-dom';
import {
    FaPlus,
    FaMinus,
    FaImage,
    FaClock,
    FaCoins,
    FaQuestionCircle,
    FaCalendarAlt,
    FaTrophy,
    FaSave,
    FaEdit,
    FaInfoCircle,
    FaChartBar,
    FaHeart,
    FaBolt,
    FaCheckCircle,
    FaTrash,
    FaUpload,
    FaSpinner,
    FaUser,
    FaCode,
    FaEye,
    FaExternalLinkAlt,
    FaLink,
    FaRedo
} from 'react-icons/fa';
import {
    MdDashboard,
    MdSchedule,
    MdTimer,
    MdQuiz,
    MdCategory,
    MdSettings,
    MdAccessTime,
    MdLiveTv,
    MdContentCopy,
    MdClose,
    MdRefresh
} from 'react-icons/md';
import {
    IoStatsChart,
    IoTime,
    IoTrophy
} from 'react-icons/io5';
import {
    HiSparkles,
    HiLightningBolt
} from 'react-icons/hi';
import QuizRulesGenerator from '../../components/QuizRulesGenerator';

const QuizAdd = () => {
    const [loader, setLoader] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        totalCoin: 0,
        questionTiming: 5,
        winCoins: [{ rank: 1, coin: 0 }],
        correctAnswerCoins: [{ correctAnswers: 1, coin: 0 }],
        type: 'live',
        totalQuestion: 0,
        questionDistribution: {
            easy: 0,
            medium: 0,
            hard: 0
        },
        scheduleTime: '',
        quizEndTime: '', // New field for the end time date picker
        status: 'pending',
        lifeLine: 0,
        resultTiming: 1,
        quizTiming: 5, // This will still store the duration in minutes
        playLimit: 1,
        htmlContent: null,
        rules_url: '',
        createdAt: '',
        updatedAt: '',
        nextDaySchedule: false // Added nextDaySchedule field
    });
    const [image, setImage] = useState(null);
    const [htmlFile, setHtmlFile] = useState(null);
    const [htmlPreview, setHtmlPreview] = useState(null);
    const [showHtmlPreview, setShowHtmlPreview] = useState(false);
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const htmlPreviewRef = useRef(null);

    // Fetch quiz data if ID exists
    useEffect(() => {
        if (id) {
            fetchQuizData();
        }
    }, [id]);

    const fetchQuizData = async () => {
        setLoader(true);
        try {
            const response = await Calling.getQuiz('', id);
            if (response.status) {
                const quizData = response.data;

                // Process data for daily quizzes with correct format
                if (quizData.type === 'daily') {
                    // For daily quizzes, convert winCoins to correctAnswerCoins format
                    const correctAnswerCoins = quizData.winCoins.map(wc => ({
                        correctAnswers: parseInt(wc.rank),
                        coin: parseInt(wc.coin)
                    }));

                    setFormData({
                        title: quizData.title,
                        description: quizData.description,
                        totalCoin: quizData.totalCoin,
                        questionTiming: quizData.questionTiming,
                        winCoins: [{ rank: 1, coin: 0 }], // Default value for live quizzes
                        correctAnswerCoins: correctAnswerCoins,
                        type: quizData.type,
                        totalQuestion: quizData.totalQuestion,
                        questionDistribution: quizData.questionDistribution,
                        scheduleTime: '',
                        quizEndTime: '',
                        status: quizData.status, // Preserve original status
                        // Preserve original timing or default to 24 hours (1440 minutes)
                        resultTiming: quizData.resultTiming || 1440,
                        quizTiming: quizData.quizTiming || 1440,
                        lifeLine: quizData.lifeLine,
                        image: quizData.image,
                        playLimit: quizData.playLimit || 1,
                        // Direct HTML content handling
                        htmlContent: quizData.htmlContent || null,
                        rules_url: quizData.rules_url || '',
                        createdAt: quizData.createdAt || '',
                        updatedAt: quizData.updatedAt || '',
                        nextDaySchedule: false // Daily quizzes don't have this feature
                    });

                    // Set HTML preview based on available data
                    if (quizData.htmlContent) {
                        setHtmlPreview(quizData.htmlContent);
                    } else if (quizData.rules_url) {
                        setHtmlPreview(quizData.rules_url);
                    }
                } else {
                    // For live quizzes, use standard format
                    const scheduleTime = quizData.scheduleTime ? new Date(quizData.scheduleTime).toISOString().slice(0, 16) : '';

                    // Calculate quiz end time based on schedule time and quiz timing
                    let quizEndTime = '';
                    if (scheduleTime && quizData.quizTiming) {
                        const startDate = new Date(quizData.scheduleTime);
                        const endDate = new Date(startDate.getTime() + quizData.quizTiming * 60000);
                        quizEndTime = endDate.toISOString().slice(0, 16);
                    }

                    setFormData({
                        title: quizData.title,
                        description: quizData.description,
                        totalCoin: quizData.totalCoin,
                        questionTiming: quizData.questionTiming,
                        winCoins: quizData.winCoins || [{ rank: 1, coin: 0 }],
                        correctAnswerCoins: [{ correctAnswers: 1, coin: 0 }], // Default value for daily quizzes
                        type: quizData.type,
                        totalQuestion: quizData.totalQuestion,
                        questionDistribution: quizData.questionDistribution,
                        scheduleTime: scheduleTime,
                        quizEndTime: quizEndTime,
                        status: quizData.status,
                        resultTiming: quizData.resultTiming || 1,
                        quizTiming: quizData.quizTiming || 5,
                        lifeLine: quizData.lifeLine,
                        image: quizData.image,
                        playLimit: quizData.playLimit || 1,
                        // Direct HTML content handling
                        htmlContent: quizData.htmlContent || null,
                        rules_url: quizData.rules_url || '',
                        createdAt: quizData.createdAt || '',
                        updatedAt: quizData.updatedAt || '',
                        nextDaySchedule: quizData.nextDaySchedule || false // Get nextDaySchedule value from API
                    });

                    // Set HTML preview based on available data
                    if (quizData.htmlContent) {
                        setHtmlPreview(quizData.htmlContent);
                    } else if (quizData.rules_url) {
                        setHtmlPreview(quizData.rules_url);
                    }
                }
            } else {
                toast.error('Failed to fetch quiz data');
            }
        } catch (error) {
            console.error('Error fetching quiz:', error);
            toast.error('Error fetching quiz data');
        } finally {
            setLoader(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoader(true);

        try {
            const quizFormData = new FormData();

            // Add all form fields
            quizFormData.append('title', formData.title);
            quizFormData.append('description', formData.description);
            quizFormData.append('totalCoin', formData.totalCoin);
            quizFormData.append('questionTiming', formData.questionTiming);
            quizFormData.append('type', formData.type);
            quizFormData.append('totalQuestion', formData.totalQuestion);
            quizFormData.append('status', formData.status);
            quizFormData.append('resultTiming', formData.resultTiming);

            // Calculate quizTiming from date picker values for live quizzes
            if (formData.type === 'live' && formData.scheduleTime && formData.quizEndTime) {
                const startDate = new Date(formData.scheduleTime);
                const endDate = new Date(formData.quizEndTime);
                const durationInMinutes = Math.round((endDate - startDate) / (1000 * 60));
                quizFormData.append('quizTiming', durationInMinutes > 0 ? durationInMinutes : 5);
            } else {
                quizFormData.append('quizTiming', formData.quizTiming);
            }

            quizFormData.append('lifeLine', formData.lifeLine);

            // Add nextDaySchedule only for live quizzes
            if (formData.type === 'live') {
                quizFormData.append('nextDaySchedule', formData.nextDaySchedule);
            }

            // Add playLimit for daily quizzes
            if (formData.type === 'daily') {
                quizFormData.append('playLimit', formData.playLimit);
            }

            // Add winCoins array for both quiz types
            // For daily quizzes, "rank" represents the correct answers threshold
            const winCoinsArray = formData.type === 'live'
                ? formData.winCoins.map(wc => ({
                    rank: parseInt(wc.rank),
                    coin: parseInt(wc.coin)
                }))
                : formData.correctAnswerCoins.map(cac => ({
                    rank: parseInt(cac.correctAnswers),
                    coin: parseInt(cac.coin)
                }));

            quizFormData.append('winCoins', JSON.stringify(winCoinsArray));

            // Add questionDistribution
            quizFormData.append('questionDistribution', JSON.stringify({
                easy: parseInt(formData.questionDistribution.easy),
                medium: parseInt(formData.questionDistribution.medium),
                hard: parseInt(formData.questionDistribution.hard)
            }));

            // Add scheduleTime if present
            if (formData.scheduleTime) {
                const localDate = new Date(formData.scheduleTime);
                const offset = localDate.getTimezoneOffset();
                const adjustedDate = new Date(localDate.getTime() - (offset * 60 * 1000));
                quizFormData.append('scheduleTime', adjustedDate.toISOString());
            }

            // Add image only if it's a new upload
            if (image) {
                quizFormData.append('image', image);
            }

            // Add HTML file if selected
            if (htmlFile) {
                quizFormData.append('html', htmlFile);
            }

            // Pass existing HTML content if available (for updates)
            if (formData.htmlContent && !htmlFile) {
                quizFormData.append('htmlContent', formData.htmlContent);
            }

            // Call API based on whether it's an update or create
            const response = id
                ? await Calling.updateQuiz(id, quizFormData)
                : await Calling.insertQuiz(quizFormData);

            if (response.status) {
                toast.success(response.message);
                // Only reset form if it's a new quiz
                if (!id) {
                    setFormData({
                        title: '',
                        description: '',
                        totalCoin: 0,
                        questionTiming: 5,
                        winCoins: [{ rank: 1, coin: 0 }],
                        correctAnswerCoins: [{ correctAnswers: 1, coin: 0 }],
                        type: 'live',
                        totalQuestion: 0,
                        questionDistribution: {
                            easy: 0,
                            medium: 0,
                            hard: 0
                        },
                        scheduleTime: '',
                        quizEndTime: '',
                        status: 'pending',
                        resultTiming: 1,
                        quizTiming: 5,
                        lifeLine: 0,
                        playLimit: 1,
                        htmlContent: null,
                        rules_url: '',
                        createdAt: '',
                        updatedAt: '',
                        nextDaySchedule: false
                    });
                    setImage(null);
                    setHtmlFile(null);
                    setHtmlPreview(null);
                    const fileInputs = document.querySelectorAll('input[type="file"]');
                    fileInputs.forEach(input => input.value = '');
                } else {
                    // Redirect to quiz show page after successful update
                    window.location.href = '/manage/quiz/show';
                }
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            console.error('Quiz submission error:', error);
            toast.error(`Failed to ${id ? 'update' : 'create'} quiz`);
        } finally {
            setLoader(false);
        }
    };

    // Update validateForm function
    const validateForm = () => {
        // Check if total questions match distribution
        const { easy, medium, hard } = formData.questionDistribution;
        const distributionTotal = easy + medium + hard;

        if (distributionTotal === 0) {
            toast.error('Please set at least one question in the distribution');
            return false;
        }

        if (formData.totalQuestion !== distributionTotal) {
            toast.error('Total questions must match the sum of easy, medium, and hard questions');
            return false;
        }

        // Check if total coins match distribution based on quiz type
        if (formData.type === 'live') {
            const totalCoinsFromRanks = formData.winCoins.reduce((sum, rank) => sum + (parseInt(rank.coin) || 0), 0);
            if (formData.totalCoin !== totalCoinsFromRanks) {
                toast.error('Total coins must match the sum of all win coin ranks');
                return false;
            }
        } else {
            const totalCoinsFromCorrectAnswers = formData.correctAnswerCoins.reduce(
                (sum, item) => sum + (parseInt(item.coin) || 0), 0
            );
            if (formData.totalCoin !== totalCoinsFromCorrectAnswers) {
                toast.error('Total coins must match the sum of all correct answer thresholds');
                return false;
            }
        }

        // Check if live quiz has schedule time
        if (formData.type === 'live' && !formData.scheduleTime) {
            toast.error('Schedule time is required for live quizzes');
            return false;
        }

        // Check if live quiz has end time
        if (formData.type === 'live' && !formData.quizEndTime) {
            toast.error('Quiz end time is required for live quizzes');
            return false;
        }

        // Check if end time is after start time
        if (formData.type === 'live' && formData.scheduleTime && formData.quizEndTime) {
            const startDate = new Date(formData.scheduleTime);
            const endDate = new Date(formData.quizEndTime);
            if (endDate <= startDate) {
                toast.error('Quiz end time must be after the schedule time');
                return false;
            }
        }

        // Check if image and HTML file are selected for new quiz only
        if (!id) {
            if (!image) {
                toast.error('Please select an image');
                return false;
            }

            if (!htmlFile) {
                toast.error('Please upload an HTML file');
                return false;
            }
        }

        // Validate rules URL if provided
        if (formData.rules_url && !isValidUrl(formData.rules_url)) {
            toast.error('Please enter a valid URL for rules');
            return false;
        }

        // Validate HTML file requirement for new quizzes
        if (!id && !htmlFile) {
            toast.error('Please upload an HTML file for quiz rules and content');
            return false;
        }

        return true;
    };

    const isValidUrl = (url) => {
        if (!url) return true; // Empty URL is valid (not required)
        try {
            new URL(url);
            return true;
        } catch (e) {
            return false;
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Handle checkbox inputs (for nextDaySchedule)
        if (type === 'checkbox') {
            setFormData(prev => ({
                ...prev,
                [name]: checked
            }));
            return;
        }

        // Handle other inputs
        // Convert numeric fields to integers
        const numericFields = ['questionTiming', 'totalQuestion', 'resultTiming', 'quizTiming', 'playLimit', 'lifeLine', 'totalCoin'];
        const processedValue = numericFields.includes(name) ? parseInt(value) || 0 : value;

        // Special handling for scheduleTime to auto-update end time
        if (name === 'scheduleTime' && value) {
            const selectedDateTime = new Date(value);
            const currentDateTime = new Date();

            // If the selected date is in the future, set status to pending
            const newStatus = selectedDateTime > currentDateTime ? 'pending' : formData.status;

            // Set end time (start time + current quiz duration)
            let newEndTime = '';
            if (formData.type === 'live') {
                // Use either existing quizTiming or default to 5 minutes
                const durationInMinutes = formData.quizTiming || 5;

                // Correctly create end date by directly modifying the hours and minutes
                // This avoids timezone conversion issues with toISOString()
                const startTimeStr = value; // Original input value in format "YYYY-MM-DDTHH:MM"
                const [datePart, timePart] = startTimeStr.split('T');
                const [hours, minutes] = timePart.split(':').map(Number);

                // Calculate new hours and minutes
                let newMinutes = minutes + (durationInMinutes % 60);
                let addHours = Math.floor(durationInMinutes / 60) + Math.floor(newMinutes / 60);
                newMinutes = newMinutes % 60;
                let newHours = (hours + addHours) % 24;

                // Handle day rollover if needed
                let newDatePart = datePart;
                if (hours + addHours >= 24) {
                    const nextDay = new Date(selectedDateTime);
                    nextDay.setDate(nextDay.getDate() + Math.floor((hours + addHours) / 24));
                    newDatePart = nextDay.toISOString().slice(0, 10);
                }

                // Format with leading zeros
                const formattedHours = newHours.toString().padStart(2, '0');
                const formattedMinutes = newMinutes.toString().padStart(2, '0');

                newEndTime = `${newDatePart}T${formattedHours}:${formattedMinutes}`;
            }

            setFormData(prev => ({
                ...prev,
                [name]: processedValue,
                status: newStatus,
                quizEndTime: newEndTime
            }));
            return;
        }

        // Special handling for quizTiming to update quizEndTime when duration changes directly
        if (name === 'quizTiming' && value) {
            // Enforce maximum quiz duration of 20 hours (1200 minutes)
            const maxDuration = 1200; // 20 hours in minutes
            const durationInMinutes = Math.min(maxDuration, parseInt(value) || 5);

            // Show notification if value was capped
            if (parseInt(value) > maxDuration) {
                toast.info('Maximum quiz duration is 20 hours');
            }

            // Parse start time directly from the string format
            const startTimeStr = formData.scheduleTime;
            if (!startTimeStr) {
                setFormData(prev => ({
                    ...prev,
                    [name]: durationInMinutes
                }));
                return;
            }

            const [datePart, timePart] = startTimeStr.split('T');
            const [hours, minutes] = timePart.split(':').map(Number);

            // Calculate new hours and minutes
            let newMinutes = minutes + (durationInMinutes % 60);
            let addHours = Math.floor(durationInMinutes / 60) + Math.floor(newMinutes / 60);
            newMinutes = newMinutes % 60;
            let newHours = (hours + addHours) % 24;

            // Handle day rollover if needed
            let newDatePart = datePart;
            if (hours + addHours >= 24) {
                const startDate = new Date(formData.scheduleTime);
                const nextDay = new Date(startDate);
                nextDay.setDate(nextDay.getDate() + Math.floor((hours + addHours) / 24));
                newDatePart = nextDay.toISOString().slice(0, 10);
            }

            // Format with leading zeros
            const formattedHours = newHours.toString().padStart(2, '0');
            const formattedMinutes = newMinutes.toString().padStart(2, '0');

            const newEndTime = `${newDatePart}T${formattedHours}:${formattedMinutes}`;

            setFormData(prev => ({
                ...prev,
                [name]: durationInMinutes,
                quizEndTime: newEndTime
            }));
            return;
        }

        // Special handling for quizEndTime to calculate and update quizTiming
        if (name === 'quizEndTime' && value && formData.scheduleTime) {
            const startDate = new Date(formData.scheduleTime);
            const endDate = new Date(value);
            const durationInMinutes = Math.round((endDate - startDate) / (1000 * 60));

            // Enforce maximum quiz duration of 20 hours (1200 minutes)
            const maxDuration = 1200; // 20 hours in minutes

            // If the calculated duration exceeds the maximum
            if (durationInMinutes > maxDuration) {
                toast.info('Maximum quiz duration is 20 hours');

                // Calculate new end time based on max duration
                const maxEndDate = new Date(startDate.getTime() + (maxDuration * 60000));
                const maxEndTime = maxEndDate.toISOString().slice(0, 16);

                setFormData(prev => ({
                    ...prev,
                    quizTiming: maxDuration,
                    quizEndTime: maxEndTime
                }));
                return;
            }

            // Only update if we have a positive duration
            if (durationInMinutes > 0) {
                setFormData(prev => ({
                    ...prev,
                    [name]: processedValue,
                    quizTiming: durationInMinutes
                }));
            } else {
                // If end time is before or equal to start time, just update the field without changing duration
                setFormData(prev => ({
                    ...prev,
                    [name]: processedValue
                }));
            }
            return;
        }

        // Default handling for other fields
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
    };

    const handleDistributionChange = (e) => {
        const { name, value } = e.target;
        const newValue = parseInt(value) || 0;

        setFormData(prev => {
            const newDistribution = {
                ...prev.questionDistribution,
                [name]: newValue
            };

            // Automatically calculate and update totalQuestion
            const newTotal = Object.values(newDistribution).reduce((sum, val) => sum + val, 0);

            return {
                ...prev,
                questionDistribution: newDistribution,
                totalQuestion: newTotal
            };
        });
    };

    const handleWinCoinsChange = (index, field, value) => {
        setFormData(prev => {
            const newWinCoins = [...prev.winCoins];
            const newValue = parseInt(value) || 0;
            newWinCoins[index] = {
                ...newWinCoins[index],
                [field]: newValue
            };

            // Automatically calculate and update totalCoin
            const newTotalCoin = newWinCoins.reduce((sum, rank) => sum + (parseInt(rank.coin) || 0), 0);

            return {
                ...prev,
                winCoins: newWinCoins,
                totalCoin: newTotalCoin
            };
        });
    };

    const handleCorrectAnswerCoinsChange = (index, field, value) => {
        setFormData(prev => {
            const newCorrectAnswerCoins = [...prev.correctAnswerCoins];
            const newValue = parseInt(value) || 0;
            newCorrectAnswerCoins[index] = {
                ...newCorrectAnswerCoins[index],
                [field]: newValue
            };

            // Automatically calculate and update totalCoin
            const newTotalCoin = newCorrectAnswerCoins.reduce(
                (sum, item) => sum + (parseInt(item.coin) || 0), 0
            );

            return {
                ...prev,
                correctAnswerCoins: newCorrectAnswerCoins,
                totalCoin: newTotalCoin
            };
        });
    };

    const addWinCoinRank = () => {
        setFormData(prev => ({
            ...prev,
            winCoins: [...prev.winCoins, { rank: prev.winCoins.length + 1, coin: 0 }]
        }));
    };

    const addCorrectAnswerCoin = () => {
        setFormData(prev => ({
            ...prev,
            correctAnswerCoins: [
                ...prev.correctAnswerCoins,
                {
                    correctAnswers: prev.correctAnswerCoins.length > 0
                        ? prev.correctAnswerCoins[prev.correctAnswerCoins.length - 1].correctAnswers + 5
                        : 5,
                    coin: 0
                }
            ]
        }));
    };

    // Add a dedicated function for duration manipulation
    const adjustQuizDuration = (minutes) => {
        if (!formData.scheduleTime) {
            toast.warning('Please set a start time first');
            return;
        }

        const startDate = new Date(formData.scheduleTime);
        const currentDuration = formData.quizTiming || 5;
        // Ensure the new duration doesn't exceed the maximum of 20 hours (1200 minutes)
        const maxDuration = 1200; // 20 hours in minutes
        const newDuration = Math.min(maxDuration, Math.max(5, currentDuration + minutes)); // Ensure between 5 minutes and 20 hours

        if (newDuration === maxDuration && currentDuration + minutes > maxDuration) {
            toast.info('Maximum quiz duration is 20 hours');
        }

        const endDate = new Date(startDate.getTime() + (newDuration * 60000));
        const newEndTime = endDate.toISOString().slice(0, 16);

        setFormData(prev => ({
            ...prev,
            quizTiming: newDuration,
            quizEndTime: newEndTime
        }));
    };

    const removeWinCoinRank = (index) => {
        if (formData.winCoins.length > 1) {
            setFormData(prev => {
                const updatedWinCoins = prev.winCoins.filter((_, i) => i !== index);
                const newTotalCoin = updatedWinCoins.reduce((sum, rank) => sum + (parseInt(rank.coin) || 0), 0);

                return {
                    ...prev,
                    winCoins: updatedWinCoins,
                    totalCoin: newTotalCoin
                };
            });
        }
    };

    const removeCorrectAnswerCoin = (index) => {
        if (formData.correctAnswerCoins.length > 1) {
            setFormData(prev => {
                const updatedCorrectAnswerCoins = prev.correctAnswerCoins.filter((_, i) => i !== index);
                const newTotalCoin = updatedCorrectAnswerCoins.reduce(
                    (sum, item) => sum + (parseInt(item.coin) || 0), 0
                );

                return {
                    ...prev,
                    correctAnswerCoins: updatedCorrectAnswerCoins,
                    totalCoin: newTotalCoin
                };
            });
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (1MB = 1024 * 1024 bytes)
            if (file.size > 1 * 1024 * 1024) {
                toast.error('Image size must not exceed 1MB');
                e.target.value = '';
                return;
            }

            // Check file type
            if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
                toast.error('Only JPG, JPEG, and PNG images are allowed');
                e.target.value = '';
                return;
            }

            setImage(file);
        }
    };

    const handleHtmlFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Check file size (1MB = 1 * 1024 * 1024 bytes)
            if (file.size > 1 * 1024 * 1024) {
                toast.error('HTML file size must not exceed 1MB');
                e.target.value = '';
                return;
            }

            // Check file type
            if (file.type !== 'text/html' && !file.name.endsWith('.html')) {
                toast.error('Only HTML files are allowed');
                e.target.value = '';
                return;
            }

            setHtmlFile(file);

            // Read the file for preview
            const reader = new FileReader();
            reader.onload = (e) => {
                setHtmlPreview(e.target.result);
            };
            reader.readAsText(file);
        }
    };

    const handleTypeChange = (e) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            type: value,
            // Set default values based on type
            ...(value === 'daily' && {
                scheduleTime: '',
                quizEndTime: '',
                resultTiming: 1440, // 24 hours
                quizTiming: 1440, // 24 hours
                // Keep winCoins but also set up correctAnswerCoins as a fallback
                correctAnswerCoins: prev.correctAnswerCoins.length ? prev.correctAnswerCoins : [{ correctAnswers: 1, coin: 0 }],
                totalCoin: prev.type === 'live'
                    ? prev.winCoins.reduce((sum, rank) => sum + (parseInt(rank.coin) || 0), 0)
                    : prev.correctAnswerCoins.reduce((sum, item) => sum + (parseInt(item.coin) || 0), 0),
                playLimit: prev.playLimit || 1,
                // For daily quizzes, only allow active or finish status
                status: id ? (prev.status === 'active' || prev.status === 'finish' ? prev.status : 'active') : 'pending',
                nextDaySchedule: false // Reset nextDaySchedule for daily quizzes
            }),
            ...(value === 'live' && {
                scheduleTime: '',
                quizEndTime: '',
                resultTiming: 1,
                quizTiming: 5,
                // Keep correctAnswerCoins but also set up winCoins as a fallback
                winCoins: prev.winCoins.length ? prev.winCoins : [{ rank: 1, coin: 0 }],
                totalCoin: prev.type === 'daily'
                    ? prev.correctAnswerCoins.reduce((sum, item) => sum + (parseInt(item.coin) || 0), 0)
                    : prev.winCoins.reduce((sum, rank) => sum + (parseInt(rank.coin) || 0), 0),
                // For live quizzes, default to pending
                status: id ? prev.status : 'pending'
                // Keep existing nextDaySchedule value
            })
        }));
    };

    const getDifficultyColor = (level) => {
        switch (level) {
            case 'easy': return 'from-green-500 to-emerald-600';
            case 'medium': return 'from-yellow-500 to-orange-600';
            case 'hard': return 'from-red-500 to-red-600';
            default: return 'from-gray-500 to-gray-600';
        }
    };

    const getDifficultyIcon = (level) => {
        switch (level) {
            case 'easy': return <FaCheckCircle className="w-5 h-5" />;
            case 'medium': return <FaClock className="w-5 h-5" />;
            case 'hard': return <HiLightningBolt className="w-5 h-5" />;
            default: return <FaQuestionCircle className="w-5 h-5" />;
        }
    };

    const openHtmlPreview = () => {
        setShowHtmlPreview(true);
    };

    const closeHtmlPreview = () => {
        setShowHtmlPreview(false);
    };

    // Calculate the quiz duration in a human-readable format
    const getQuizDurationText = () => {
        if (!formData.scheduleTime || !formData.quizEndTime) return '';

        const startDate = new Date(formData.scheduleTime);
        const endDate = new Date(formData.quizEndTime);
        const durationInMinutes = Math.round((endDate - startDate) / (1000 * 60));

        if (durationInMinutes < 60) {
            return `${durationInMinutes} minute${durationInMinutes !== 1 ? 's' : ''}`;
        } else {
            const hours = Math.floor(durationInMinutes / 60);
            const minutes = durationInMinutes % 60;
            return `${hours} hour${hours !== 1 ? 's' : ''}${minutes > 0 ? ` ${minutes} minute${minutes !== 1 ? 's' : ''}` : ''}`;
        }
    };

    // HTML Preview Modal with enhanced handling for both inline HTML and URLs
    const HtmlPreviewModal = () => {
        const [isLoading, setIsLoading] = useState(true);
        const [previewType, setPreviewType] = useState('content');

        useEffect(() => {
            // Determine the type of preview based on content
            if (htmlPreview) {
                if (htmlPreview.startsWith('http')) {
                    setPreviewType('url');
                } else {
                    setPreviewType('content');
                }
            }
        }, []);

        const handleIframeLoad = () => {
            setIsLoading(false);
        };

        return (
            <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                    <div className="p-4 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-xl">
                        <h3 className="text-xl font-bold text-indigo-800 flex items-center gap-2">
                            <FaCode className="w-5 h-5" />
                            HTML Content & Rules Preview
                        </h3>
                        <div className="flex items-center gap-2">
                            {previewType === 'url' && (
                                <a
                                    href={htmlPreview}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-blue-100 text-blue-500 rounded-lg transition-colors flex items-center gap-1"
                                    title="Open in new tab"
                                >
                                    <FaExternalLinkAlt className="w-4 h-4" />
                                    <span className="text-sm">Open</span>
                                </a>
                            )}
                            <button
                                onClick={closeHtmlPreview}
                                className="p-2 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
                                title="Close preview"
                            >
                                <MdClose className="w-6 h-6" />
                            </button>
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto p-2 relative">
                        {isLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-70 z-10">
                                <div className="flex flex-col items-center">
                                    <FaSpinner className="w-10 h-10 text-indigo-600 animate-spin mb-2" />
                                    <p className="text-indigo-800 font-medium">Loading preview...</p>
                                </div>
                            </div>
                        )}
                        <div
                            ref={htmlPreviewRef}
                            className="bg-white rounded border-2 border-gray-200 w-full h-full min-h-[300px] overflow-auto"
                        >
                            {previewType === 'url' ? (
                                <iframe
                                    src={htmlPreview}
                                    title="HTML Preview"
                                    className="w-full h-full min-h-[500px]"
                                    sandbox="allow-scripts"
                                    onLoad={handleIframeLoad}
                                />
                            ) : (
                                <iframe
                                    srcDoc={htmlPreview}
                                    title="HTML Preview"
                                    className="w-full h-full min-h-[500px]"
                                    sandbox="allow-scripts"
                                    onLoad={handleIframeLoad}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Format datetime for display
    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return '';
        const date = new Date(dateTimeStr);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Helper component for HTML content display
    const HtmlContentDisplay = () => {
        if (!formData.rules_url && !formData.htmlContent) {
            return null; // Don't show anything if no HTML content or rules URL
        }

        return (
            <div className="mb-4 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                        <div className="flex items-center gap-2 text-sm text-slate-600 mb-1 font-medium">
                            <FaCheckCircle className="w-4 h-4 text-green-500" />
                            {formData.rules_url ? (
                                <p>HTML file: {formData.rules_url.split('/').pop()}</p>
                            ) : (
                                <p>HTML content available</p>
                            )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                                <FaCalendarAlt className="w-3 h-3" />
                                Created: {formatDateTime(formData.createdAt)}
                            </span>
                            {formData.updatedAt && (
                                <span className="flex items-center gap-1">
                                    <FaClock className="w-3 h-3" />
                                    Updated: {formatDateTime(formData.updatedAt)}
                                </span>
                            )}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={openHtmlPreview}
                        className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-medium flex items-center gap-1 hover:bg-indigo-200 transition-colors"
                    >
                        <FaEye className="w-3 h-3" />
                        Preview
                    </button>
                </div>
                <p className="text-xs text-slate-500 mt-2">Upload a new file to replace the current HTML content</p>
            </div>
        );
    };

    return (
        <div className="w-full text-main-font mt-16 px-3 md:ml-[4.4rem]">
            <h1 className="text-xl md:text-2xl font-bold mt-4 text-main-font w-auto">Quiz Management</h1>
            <div className='bg-main-white rounded-xl shadow-lg'>
                <Navbar selectState={'quizadd'} />
                <hr className='border-main-border' />

                <div className="p-6">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-2xl p-6 mb-8 border border-indigo-100">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl">
                                {id ? <FaEdit className="w-6 h-6 text-white" /> : <MdQuiz className="w-6 h-6 text-white" />}
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold text-slate-800">
                                    {id ? 'Edit Quiz' : 'Create New Quiz'}
                                </h2>
                                <p className="text-slate-600">
                                    {id ? 'Update your quiz configuration' : 'Set up a new quiz with all the details'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        {/* Basic Information & Quiz Settings */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Basic Information */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl">
                                        <FaInfoCircle className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Basic Information</h3>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                            <MdQuiz className="w-4 h-4" />
                                            Quiz Title *
                                        </label>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                            placeholder="Enter an engaging quiz title"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                            <FaInfoCircle className="w-4 h-4" />
                                            Description *
                                        </label>
                                        <textarea
                                            name="description"
                                            value={formData.description}
                                            onChange={handleChange}
                                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                            rows="4"
                                            placeholder="Describe what this quiz is about..."
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                            <FaImage className="w-4 h-4" />
                                            {id ? 'Quiz Image (Optional)' : 'Quiz Image * (Max 1MB)'}
                                        </label>

                                        {id && formData.image && (
                                            <div className="mb-4 p-4 bg-gray-50 rounded-xl border-2 border-gray-200">
                                                <p className="text-sm text-slate-600 mb-3 font-medium">Current Image:</p>
                                                <img
                                                    src={formData.image}
                                                    alt="Current quiz image"
                                                    className="w-full max-w-sm h-32 object-cover rounded-xl border-2 border-gray-300 shadow-sm"
                                                />
                                            </div>
                                        )}

                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                                required={!id}
                                            />
                                            <FaUpload className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                        </div>
                                    </div>

                                    {/* HTML Content Upload */}
                                    <div className="pt-4 border-t-2 border-gray-100">
                                        <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                            <FaCode className="w-4 h-4" />
                                            HTML Content & Rules {!id && '*'}
                                        </label>

                                        {id && (formData.htmlContent || formData.rules_url) && (
                                            <HtmlContentDisplay />
                                        )}

                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept=".html"
                                                onChange={handleHtmlFileChange}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                                required={!id}
                                            />
                                            <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2">
                                                <FaCode className="text-gray-400 w-5 h-5" />
                                                {(htmlFile || htmlPreview) && (
                                                    <button
                                                        type="button"
                                                        onClick={openHtmlPreview}
                                                        className="text-indigo-600 hover:text-indigo-800"
                                                        title="Preview HTML"
                                                    >
                                                        <FaEye className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <p className="mt-2 text-xs text-slate-500">
                                            Upload an HTML file (max 1MB) that contains both the quiz rules and any interactive content.
                                            {!id && ' Required for new quizzes.'}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Quiz Settings */}
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl">
                                        <MdSettings className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Quiz Configuration</h3>
                                </div>

                                <div className="space-y-6">
                                    <div>
                                        <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                            <MdCategory className="w-4 h-4" />
                                            Quiz Type *
                                        </label>
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                type="button"
                                                onClick={() => handleTypeChange({ target: { value: 'daily' } })}
                                                className={`p-4 rounded-xl border-2 transition-all duration-300 flex items-center gap-3 ${formData.type === 'daily'
                                                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                                                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-blue-300'
                                                    }`}
                                                disabled={id}
                                            >
                                                <FaCalendarAlt className="w-5 h-5" />
                                                <span className="font-semibold">Daily</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleTypeChange({ target: { value: 'live' } })}
                                                className={`p-4 rounded-xl border-2 transition-all duration-300 flex items-center gap-3 ${formData.type === 'live'
                                                    ? 'border-red-500 bg-red-50 text-red-700'
                                                    : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-red-300'
                                                    }`}
                                                disabled={id}
                                            >
                                                <MdLiveTv className="w-5 h-5" />
                                                <span className="font-semibold">Live</span>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <MdTimer className="w-4 h-4" />
                                                Per Question (sec) *
                                            </label>
                                            <input
                                                type="number"
                                                name="questionTiming"
                                                value={formData.questionTiming}
                                                onChange={handleChange}
                                                min="5"
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <FaHeart className="w-4 h-4" />
                                                Life Lines *
                                            </label>
                                            <input
                                                type="number"
                                                name="lifeLine"
                                                value={formData.lifeLine}
                                                onChange={handleChange}
                                                min="0"
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                required
                                            />
                                        </div>
                                    </div>

                                    {/* Auto-Reschedule Option (only for live quizzes) */}
                                    {formData.type === 'live' && (
                                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-indigo-100">
                                            <div className="flex items-center gap-3">
                                                <div className="flex flex-col flex-1">
                                                    <label className="flex items-center gap-2 mb-1 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            name="nextDaySchedule"
                                                            checked={formData.nextDaySchedule}
                                                            onChange={handleChange}
                                                            className="w-5 h-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="text-sm font-semibold text-indigo-800 flex items-center gap-2">
                                                            <MdRefresh className="w-4 h-4" />
                                                            Auto-Reschedule Quiz
                                                        </span>
                                                    </label>
                                                    <p className="text-xs text-indigo-600 ml-7">
                                                        When enabled, this quiz will automatically be rescheduled for the next day after completion
                                                    </p>
                                                </div>
                                                <div className="p-2 bg-indigo-100 text-indigo-700 rounded-full">
                                                    <FaRedo className="w-5 h-5" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {formData.type === 'daily' && (
                                        <div>
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <FaUser className="w-4 h-4" />
                                                Play Limit Per User *
                                            </label>
                                            <input
                                                type="number"
                                                name="playLimit"
                                                value={formData.playLimit}
                                                onChange={handleChange}
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                required
                                            />
                                        </div>
                                    )}

                                    {formData.type === 'live' && (
                                        <>
                                            {/* Here's where we show the calculated quiz duration */}
                                            {formData.scheduleTime && formData.quizEndTime && (
                                                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-indigo-100">
                                                    <h4 className="text-sm font-semibold text-indigo-800 mb-2 flex items-center gap-2">
                                                        <IoTime className="w-4 h-4" />
                                                        Quiz Duration
                                                    </h4>
                                                    <p className="text-indigo-700 font-medium flex items-center gap-2">
                                                        <FaClock className="w-4 h-4" />
                                                        {getQuizDurationText()}
                                                    </p>
                                                    <p className="text-xs text-indigo-600 mt-2">
                                                        Start: {formatDateTime(formData.scheduleTime)}
                                                        <br />
                                                        End: {formatDateTime(formData.quizEndTime)}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Question Distribution */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="p-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
                                    <FaChartBar className="w-6 h-6 text-white" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-800">Question Distribution</h3>
                            </div>
                            <p className="text-sm text-red-500 mb-6">
                                Note: Easy questions are used only in <span className="font-bold">Quiz Non-Stop</span>. For better gameplay experience, we recommend using <span className="font-bold">Medium</span> and <span className="font-bold">Hard</span> questions here.
                            </p>


                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                {['easy', 'medium', 'hard'].map(level => (
                                    <div key={level} className="relative">
                                        <label className="text-sm font-semibold mb-3 text-slate-700 capitalize flex items-center gap-2">
                                            <div className={`p-2 bg-gradient-to-r ${getDifficultyColor(level)} rounded-lg`}>
                                                <div className="text-white">
                                                    {getDifficultyIcon(level)}
                                                </div>
                                            </div>
                                            {level} Questions
                                        </label>
                                        <input
                                            type="number"
                                            name={level}
                                            value={formData.questionDistribution[level]}
                                            onChange={handleDistributionChange}
                                            min="0"
                                            onWheel={(e) => e.target.blur()}
                                            className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                            required
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border-2 border-indigo-200">
                                <div className="flex items-center justify-center gap-3">
                                    <FaQuestionCircle className="w-6 h-6 text-indigo-600" />
                                    <span className="text-xl font-bold text-indigo-800">
                                        Total Questions: {formData.totalQuestion}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Prize Distribution - Based on Quiz Type */}
                        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="p-3 bg-gradient-to-r from-yellow-500 to-orange-600 rounded-xl">
                                        <FaTrophy className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">
                                        {formData.type === 'live' ? 'Prize Distribution by Rank' : 'Rewards for Correct Answers'}
                                    </h3>
                                </div>
                                <button
                                    type="button"
                                    onClick={formData.type === 'live' ? addWinCoinRank : addCorrectAnswerCoin}
                                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl font-semibold"
                                >
                                    <FaPlus className="w-4 h-4" />
                                    {formData.type === 'live' ? 'Add Rank' : 'Add Reward Level'}
                                </button>
                            </div>

                            {/* Live Quiz - Rank-based distribution */}
                            {formData.type === 'live' && (
                                <div className="space-y-4 mb-6">
                                    {formData.winCoins.map((winCoin, index) => (
                                        <div key={index} className="flex gap-4 items-end p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                                            <div className="flex-1">
                                                <label className="text-sm font-semibold mb-2 text-slate-700 flex items-center gap-2">
                                                    <IoTrophy className="w-4 h-4" />
                                                    Rank Position
                                                </label>
                                                <input
                                                    type="number"
                                                    value={winCoin.rank}
                                                    onChange={(e) => handleWinCoinsChange(index, 'rank', e.target.value)}
                                                    min="1"
                                                    onWheel={(e) => e.target.blur()}
                                                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-white"
                                                    required
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-sm font-semibold mb-2 text-slate-700 flex items-center gap-2">
                                                    <FaCoins className="w-4 h-4" />
                                                    Coins Reward
                                                </label>
                                                <input
                                                    type="number"
                                                    value={winCoin.coin}
                                                    onChange={(e) => handleWinCoinsChange(index, 'coin', e.target.value)}
                                                    min="0"
                                                    onWheel={(e) => e.target.blur()}

                                                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-white"
                                                    required
                                                />
                                            </div>
                                            {formData.winCoins.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeWinCoinRank(index)}
                                                    className="p-3 text-red-500 hover:bg-red-50 rounded-lg transition-all duration-300 hover:shadow-md"
                                                    title="Remove this rank"
                                                >
                                                    <FaTrash className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Daily Quiz - Correct Answers based distribution */}
                            {formData.type === 'daily' && (
                                <div className="space-y-4 mb-6">
                                    {formData.correctAnswerCoins.map((item, index) => (
                                        <div key={index} className="flex gap-4 items-end p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border-2 border-gray-200">
                                            <div className="flex-1">
                                                <label className="text-sm font-semibold mb-2 text-slate-700 flex items-center gap-2">
                                                    <FaCheckCircle className="w-4 h-4" />
                                                    Correct Answers (Min)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.correctAnswers}
                                                    onChange={(e) => handleCorrectAnswerCoinsChange(index, 'correctAnswers', e.target.value)}
                                                    min="1"
                                                    max={formData.totalQuestion}
                                                    onWheel={(e) => e.target.blur()}

                                                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-white"
                                                    required
                                                />
                                            </div>
                                            <div className="flex-1">
                                                <label className="text-sm font-semibold mb-2 text-slate-700 flex items-center gap-2">
                                                    <FaCoins className="w-4 h-4" />
                                                    Coins Reward
                                                </label>
                                                <input
                                                    type="number"
                                                    value={item.coin}
                                                    onChange={(e) => handleCorrectAnswerCoinsChange(index, 'coin', e.target.value)}
                                                    min="0"
                                                    onWheel={(e) => e.target.blur()}

                                                    className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-white"
                                                    required
                                                />
                                            </div>
                                            {formData.correctAnswerCoins.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeCorrectAnswerCoin(index)}
                                                    className="p-3 text-red-500 hover:bg-red-50 rounded-lg transition-all duration-300 hover:shadow-md"
                                                    title="Remove this threshold"
                                                >
                                                    <FaTrash className="w-5 h-5" />
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border-2 border-yellow-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <FaCoins className="w-6 h-6 text-yellow-600" />
                                        <span className="text-lg font-semibold text-yellow-800">Total Prize Pool</span>
                                    </div>
                                    <span className="text-2xl font-bold text-yellow-700">{formData.totalCoin.toLocaleString()} coins</span>
                                </div>
                            </div>

                            {/* Integrated Prize Distribution Summary */}
                            {formData.totalCoin > 0 && (
                                <div className="mt-6 p-5 bg-white rounded-xl border-2 border-indigo-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                                    <h4 className="text-base font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                                        <FaInfoCircle className="w-4 h-4" />
                                        Prize Distribution Summary
                                    </h4>

                                    <div className="mb-3 text-sm text-indigo-700">
                                        {formData.type === 'live'
                                            ? 'Players will receive coins based on their final ranking position.'
                                            : 'Players will receive coins based on how many questions they answer correctly.'}
                                    </div>

                                    <div className="space-y-2 mt-3">
                                        {formData.type === 'live' ? (
                                            // Live quiz summary with proper range handling
                                            (() => {
                                                // Sort by rank in ascending order
                                                const sortedWinCoins = [...formData.winCoins]
                                                    .map(coin => ({
                                                        rank: parseInt(coin.rank),
                                                        coin: parseInt(coin.coin)
                                                    }))
                                                    .sort((a, b) => a.rank - b.rank);

                                                // Process to create ranges
                                                const rangedResults = [];

                                                for (let i = 0; i < sortedWinCoins.length; i++) {
                                                    const currentEntry = sortedWinCoins[i];

                                                    // Special handling for rank 1
                                                    if (currentEntry.rank === 1) {
                                                        rangedResults.push({
                                                            rank: 1,
                                                            coin: currentEntry.coin,
                                                            isFirst: true
                                                        });
                                                        continue;
                                                    }

                                                    // Calculate start rank
                                                    const startRank = i > 0 ? sortedWinCoins[i - 1].rank + 1 : 1;
                                                    const endRank = currentEntry.rank;

                                                    // Format the rank
                                                    const rankDisplay = startRank === endRank
                                                        ? `${startRank}`
                                                        : `${startRank}-${endRank}`;

                                                    rangedResults.push({
                                                        rank: rankDisplay,
                                                        coin: currentEntry.coin,
                                                        isFirst: false
                                                    });
                                                }

                                                return rangedResults.map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center p-2 rounded-lg bg-white bg-opacity-70 border border-indigo-100">
                                                        <div className="flex items-center gap-2">
                                                            {item.isFirst ? (
                                                                <div className="w-6 h-6 flex items-center justify-center bg-yellow-100 text-yellow-700 rounded-full">
                                                                    <IoTrophy className="w-4 h-4" />
                                                                </div>
                                                            ) : (
                                                                <div className="w-6 h-6 flex items-center justify-center bg-blue-100 text-blue-700 rounded-full">
                                                                    <FaTrophy className="w-3 h-3" />
                                                                </div>
                                                            )}
                                                            <span className="font-medium text-slate-700">
                                                                {item.isFirst
                                                                    ? 'Winner (1st place)'
                                                                    : `Rank ${item.rank}`}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 rounded-md border border-yellow-100">
                                                            <FaCoins className="w-3 h-3 text-yellow-600" />
                                                            <span className="font-medium text-yellow-800">{item.coin}</span>
                                                        </div>
                                                    </div>
                                                ));
                                            })()
                                        ) : (
                                            // Daily quiz summary with correct answer thresholds
                                            (() => {
                                                // Sort by correct answers threshold in descending order
                                                const sortedAnswerCoins = [...formData.correctAnswerCoins]
                                                    .map(coin => ({
                                                        correctAnswers: parseInt(coin.correctAnswers),
                                                        coin: parseInt(coin.coin)
                                                    }))
                                                    .sort((a, b) => a.correctAnswers - b.correctAnswers);

                                                // Process to create ranges
                                                const rangedResults = [];

                                                for (let i = 0; i < sortedAnswerCoins.length; i++) {
                                                    const currentEntry = sortedAnswerCoins[i];

                                                    // Calculate threshold ranges
                                                    let start = currentEntry.correctAnswers;
                                                    let end;

                                                    if (i === sortedAnswerCoins.length - 1) {
                                                        // For the last threshold, extend to totalQuestion
                                                        end = formData.totalQuestion;
                                                    } else {
                                                        // For other thresholds, extend up to one less than the next threshold
                                                        end = sortedAnswerCoins[i + 1].correctAnswers - 1;
                                                    }

                                                    // Format the threshold range
                                                    const rangeDisplay = start === end
                                                        ? `${start}`
                                                        : `${start}-${end}`;

                                                    // Create display text
                                                    const displayText = `${rangeDisplay} correct answer${start === 1 && end === 1 ? '' : 's'}`;

                                                    rangedResults.push({
                                                        displayText,
                                                        coin: currentEntry.coin
                                                    });
                                                }

                                                return rangedResults.map((item, index) => (
                                                    <div key={index} className="flex justify-between items-center p-2 rounded-lg bg-white bg-opacity-70 border border-indigo-100">
                                                        <div className="flex items-center gap-2">
                                                            <div className="w-6 h-6 flex items-center justify-center bg-green-100 text-green-700 rounded-full">
                                                                <FaCheckCircle className="w-3 h-3" />
                                                            </div>
                                                            <span className="font-medium text-slate-700">
                                                                {item.displayText}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-1 px-2 py-1 bg-yellow-50 rounded-md border border-yellow-100">
                                                            <FaCoins className="w-3 h-3 text-yellow-600" />
                                                            <span className="font-medium text-yellow-800">{item.coin}</span>
                                                        </div>
                                                    </div>
                                                ));
                                            })()
                                        )}
                                    </div>

                                    {formData.type === 'live' && (
                                        <div className="mt-3 text-xs text-slate-600 italic">
                                            Players finishing below these ranks will not receive coins.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Schedule and Timing */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {formData.type === 'live' && (
                                <>
                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-3 bg-gradient-to-r from-red-500 to-pink-600 rounded-xl">
                                                <MdSchedule className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-800">Live Schedule</h3>
                                        </div>

                                        <div>
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <FaCalendarAlt className="w-4 h-4" />
                                                Schedule Start Time *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="datetime-local"
                                                    name="scheduleTime"
                                                    value={formData.scheduleTime}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="p-3 bg-gradient-to-r from-orange-500 to-red-600 rounded-xl">
                                                <MdAccessTime className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold text-slate-800">Quiz End Time</h3>
                                        </div>

                                        <div>
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <IoTime className="w-4 h-4" />
                                                Schedule End Time *
                                            </label>
                                            <div className="relative">
                                                <input
                                                    type="datetime-local"
                                                    name="quizEndTime"
                                                    value={formData.quizEndTime}
                                                    onChange={handleChange}
                                                    className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                    required
                                                />
                                            </div>
                                            <p className="mt-2 text-xs text-slate-500">
                                                Select when the quiz should end. The duration will be calculated automatically.
                                            </p>
                                        </div>

                                        <div className="mt-6">
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <MdTimer className="w-4 h-4" />
                                                Quiz Duration (minutes) *
                                                <span className="text-xs font-normal text-indigo-600 ml-1">(Max: 20 hours)</span>
                                            </label>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    name="quizTiming"
                                                    value={formData.quizTiming}
                                                    onChange={handleChange}
                                                    min="5"
                                                    max="1200"
                                                    onWheel={(e) => e.target.blur()}
                                                    className="flex-1 p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                    required
                                                />
                                                <div className="flex gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => adjustQuizDuration(-5)}
                                                        className="p-3 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                                                        title="Decrease duration by 5 minutes"
                                                    >
                                                        <FaMinus className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => adjustQuizDuration(5)}
                                                        className="p-3 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                                                        title="Increase duration by 5 minutes"
                                                    >
                                                        <FaPlus className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-2 text-blue-700">
                                                <FaInfoCircle className="w-4 h-4 flex-shrink-0" />
                                                <p className="text-sm">
                                                    Changing either the end time or duration will automatically update the other field.
                                                    {formData.scheduleTime && formData.quizEndTime && (
                                                        <span className="font-medium"> Current duration: {getQuizDurationText()}</span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="mt-6">
                                            <label className="text-sm font-semibold mb-3 text-slate-700 flex items-center gap-2">
                                                <IoStatsChart className="w-4 h-4" />
                                                Result Display Duration (minutes) *
                                            </label>
                                            <input
                                                type="number"
                                                name="resultTiming"
                                                value={formData.resultTiming}
                                                onChange={handleChange}
                                                min="1"
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                                required
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Status Selection (Only for Edit) */}
                        {id && (
                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-3 bg-gradient-to-r from-slate-500 to-slate-600 rounded-xl">
                                        <FaCheckCircle className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-800">Quiz Status</h3>
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-3 text-slate-700">Current Status *</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        className="w-full p-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-300 bg-gray-50 focus:bg-white"
                                        required
                                    >
                                        {formData.type === 'daily' ? (
                                            // For daily quizzes, only allow active or finish status
                                            <>
                                                <option value="active">Active</option>
                                                <option value="finish">Finished</option>
                                            </>
                                        ) : (
                                            // For live quizzes, allow all statuses
                                            <>
                                                <option value="pending">Pending</option>
                                                <option value="active">Active</option>
                                                <option value="completed">Completed</option>
                                                <option value="finish">Finished</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Submit Button */}
                        <div className="flex justify-end pt-6">
                            <button
                                type="submit"
                                disabled={loader}
                                className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 text-lg font-semibold flex items-center gap-3 shadow-lg hover:shadow-xl min-w-[180px] justify-center"
                            >
                                {loader ? (
                                    <>
                                        <FaSpinner className="animate-spin w-5 h-5" />
                                        {id ? 'Updating...' : 'Creating...'}
                                    </>
                                ) : (
                                    <>
                                        <FaSave className="w-5 h-5" />
                                        {id ? 'Update Quiz' : 'Create Quiz'}
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                    <QuizRulesGenerator
                        quizData={formData}
                        onDownload={(htmlContent) => {
                            // Optional: You can handle the HTML content here
                            // For example, to upload it directly to your server
                            setHtmlFile(new Blob([htmlContent], { type: 'text/html' }));
                            setHtmlPreview(htmlContent);
                        }}
                    />
                </div>
            </div>

            {/* HTML Preview Modal */}
            {showHtmlPreview && <HtmlPreviewModal />}
        </div>
    );
};

export default QuizAdd;